import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  ArrayValidator,
  CheckboxWithLabelComponent,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengPasswordDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import {
  AdminProductAttributes,
  AdminProductCharacteristics,
  AdminProductDetailsForm,
  AdminProductDetailsFormGroup,
} from '../../models/admin-product-details-form';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
  MultimediaChooseOrUploadMultipleControlComponent,
} from '@web/shared/module/multimedia';
import { AdminBrandSelectItemsProviderService } from '@web/shared/module/brand';
import { map, Observable, of, startWith, tap } from 'rxjs';
import { Select, SelectFilterEvent } from 'primeng/select';
import { AdminProductAttributesControlComponent } from '../admin-product-attributes-control/admin-product-attributes-control.component';
import { InputNumber } from 'primeng/inputnumber';
import { AdminProductStatus } from '../../enums/admin-product-status';
import { AdminProductStatusProviderService } from '../../providers/admin-product-status-provider.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminProductCharacteristicsControlComponent } from '../admin-product-characteristics-control/admin-product-characteristics-control.component';
import { AdminProductCharacteristicsValidator } from '../../validators/admin-product-characteristics.validator';
import { Checkbox } from 'primeng/checkbox';
import { Password } from 'primeng/password';
import {
  WarehouseGoodType,
  WarehouseGoodUsageStatusComponent,
  AdminWarehouseGoodDataSourceService,
  AdminWarehouseGoodSelectItemsProviderService,
  adminWarehouseGoodSelectItemsWithCurrentSelection,
} from '@web/features/warehouse';

@Component({
  selector: 'app-admin-product-details-form',
  imports: [
    AdminProductAttributesControlComponent,
    Button,
    CommonModule,
    FormControlComponent,
    FormsModule,
    InputNumber,
    InputText,
    MultimediaChooseOrUploadControlComponent,
    MultimediaChooseOrUploadMultipleControlComponent,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    AdminProductCharacteristicsControlComponent,
    Checkbox,
    CheckboxWithLabelComponent,
    Password,
    PrimengPasswordDirective,
    WarehouseGoodUsageStatusComponent,
  ],
  templateUrl: './admin-product-details-form.component.html',
  styleUrl: './admin-product-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminProductDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly brandSelectItemsProvider = inject(AdminBrandSelectItemsProviderService);
  /* prettier-ignore */
  private readonly productStatusProvider = inject(AdminProductStatusProviderService);
  /* prettier-ignore */
  private readonly warehouseGoodDataSource = inject(AdminWarehouseGoodDataSourceService);
  /* prettier-ignore */
  private readonly warehouseGoodSelectItemsProvider = inject(AdminWarehouseGoodSelectItemsProviderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /* prettier-ignore */
  public readonly canBeWarehouseGoodEdited = input<Nillable<boolean>>(true);
  public readonly data = input<Nillable<AdminProductDetailsForm>>();
  public readonly isEditContext = input<boolean>(false);
  public readonly submitClickEvent = output<AdminProductDetailsForm>();

  /* prettier-ignore */
  protected readonly productStatusSelectItems$ = this.productStatusSelectItems();
  protected readonly brandSelectItems$ = this.brandSelectItems();
  protected readonly form = this.buildForm();
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly warehouseGoodType = WarehouseGoodType;
  /* prettier-ignore */
  protected warehouseGoodSelectItems$: Observable<SelectItem<Guid>[]> = of([]);
  protected currentGoodId$!: Observable<Nillable<Guid>>;
  /* prettier-ignore */
  protected shouldPasswordVisible$!: Observable<boolean>;

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    effect(() => this.configureWarehouseGoodAvailability());
  }

  public ngOnInit(): void {
    this.listenToStatusValueChanges();
    this.configureWarehouseGoodSelectItemsSource();
    this.configureShouldPasswordVisibleSource();
    this.configureCurrentGoodIdSource();

    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleWarehouseGoodSelectFilterChange(event: SelectFilterEvent): void {
    this.warehouseGoodSelectItems$ = this.warehouseGoodSelectItems(
      event.filter.toLowerCase(),
    );

    this.changeDetectorRef.detectChanges();
  }

  private listenToStatusValueChanges(): void {
    this.form.controls.status.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        const isPrivateOrPublic = this.isPrivateOrProtectedOrPublic(status);

        this.configureFeaturedFileRequirements(isPrivateOrPublic);
        this.configureFilesRequirements(isPrivateOrPublic);
        this.configurePriceRequirements(isPrivateOrPublic);
        this.configureCharacteristicsRequirements(isPrivateOrPublic);
      });
  }

  private isPrivateOrProtectedOrPublic(
    status: Nillable<AdminProductStatus>,
  ): boolean {
    if (isNil(status)) {
      return false;
    }

    /* prettier-ignore */
    return [
      AdminProductStatus.Public,
      AdminProductStatus.Protected,
      AdminProductStatus.Private,
    ].includes(status);
  }

  private isProtected(status: Nillable<AdminProductStatus>): boolean {
    if (isNil(status)) {
      return false;
    }

    /* prettier-ignore */
    return [
      AdminProductStatus.Protected,
    ].includes(status);
  }

  private toData(): AdminProductDetailsForm {
    const data = this.form.getRawValue();

    return new AdminProductDetailsForm({
      attributes: data.attributes,
      characteristics: data.characteristics,
      cosmeticBrandId: data.cosmeticBrandId,
      featuredFile: data.featuredFile,
      files: data.files,
      name: data.name,
      password: data.password,
      price: data.price,
      slug: data.slug,
      status: valueOrDefault(data.status, AdminProductStatus.Draft),
      virtual: data.virtual,
      maxQuantityPerOrder: data.maxQuantityPerOrder,
      availableQuantity: data.availableQuantity,
      warehouseProductId: data.warehouseProductId,
    });
  }

  private buildForm(): AdminProductDetailsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      attributes: this.builder.control<Nillable<AdminProductAttributes>>(null),
      characteristics: this.builder.control<Nillable<AdminProductCharacteristics>>(null),
      cosmeticBrandId: this.builder.control<Nillable<Guid>>(null),
      featuredFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null, [Validators.required]),
      files: this.builder.control<Nillable<AdminMultimediaImageUpload[]>>(null, [Validators.required, ArrayValidator.min(AdminProductDetailsForm.FilesMinCount)]),
      name: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(AdminProductDetailsForm.NameMaxLength)]),
      password: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminProductDetailsForm.PasswordMaxLength)]),
      price: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(AdminProductDetailsForm.PriceMin)]),
      slug: this.builder.control<Nillable<string>>(null),
      status: this.builder.control<Nillable<AdminProductStatus>>(null, [Validators.required]),
      virtual: this.builder.control<Nillable<boolean>>(false),
      maxQuantityPerOrder: this.builder.control<Nillable<number>>(null, [Validators.min(AdminProductDetailsForm.MinMaxQuantityPerOrder)]),
      availableQuantity: this.builder.control<Nillable<number>>(null, [Validators.min(AdminProductDetailsForm.MinMaxAvailableQuantity)]),
      warehouseProductId: this.builder.control<Nillable<Guid>>(null),
    });
  }

  private brandSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.brandSelectItemsProvider.fetchCollectionAsSelectItems('id');
  }

  private shouldPasswordVisibleSource(): Observable<boolean> {
    return this.form.controls.status.valueChanges.pipe(
      startWith(this.form.controls.status.value),
      map((status) => this.isProtected(status)),
      tap((isProtected) => this.configurePasswordRequirements(isProtected)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private configurePasswordRequirements(required: boolean): void {
    const control = this.form.controls.password;

    this.changeRequirementsForControl({ control, required });
  }

  private configureWarehouseGoodSelectItemsSource(): void {
    this.warehouseGoodSelectItems$ = this.warehouseGoodSelectItems();
    this.changeDetectorRef.detectChanges();
  }

  private configureShouldPasswordVisibleSource() {
    this.shouldPasswordVisible$ = this.shouldPasswordVisibleSource();
    this.changeDetectorRef.detectChanges();
  }

  private configureCurrentGoodIdSource(): void {
    this.currentGoodId$ = this.form.controls.warehouseProductId.valueChanges;
    this.changeDetectorRef.detectChanges();
  }

  private warehouseGoodSelectItems(
    query: Nillable<string> = null,
  ): Observable<SelectItem<Guid>[]> {
    return adminWarehouseGoodSelectItemsWithCurrentSelection({
      control: this.form.controls.warehouseProductId,
      dataSource: this.warehouseGoodDataSource,
      selectItemsProvider: this.warehouseGoodSelectItemsProvider,
      query,
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      attributes: data?.attributes,
      characteristics: data?.characteristics,
      cosmeticBrandId: data?.cosmeticBrandId,
      featuredFile: data?.featuredFile,
      files: data?.files,
      name: data?.name,
      password: data?.password,
      price: data?.price,
      slug: data?.slug,
      status: valueOrDefault(data?.status, AdminProductStatus.Draft),
      virtual: data?.virtual,
      maxQuantityPerOrder: data?.maxQuantityPerOrder,
      availableQuantity: data?.availableQuantity,
      warehouseProductId: data?.warehouseProductId,
    });
  }

  private configureWarehouseGoodAvailability(): void {
    const control = this.form.controls.warehouseProductId;
    const canBeWarehouseGoodEdited = this.canBeWarehouseGoodEdited();

    if (isNil(canBeWarehouseGoodEdited)) {
      control.enable();

      return;
    }

    canBeWarehouseGoodEdited ? control.enable() : control.disable();
  }

  private productStatusSelectItems(): Observable<
    SelectItem<AdminProductStatus>[]
  > {
    return this.productStatusProvider.selectItems();
  }

  private configureCharacteristicsRequirements(required: boolean): void {
    const control = this.form.controls.characteristics;

    if (required) {
      control.addValidators([AdminProductCharacteristicsValidator.min(1)]);
    } else {
      control.removeValidators([AdminProductCharacteristicsValidator.min(1)]);
    }
  }

  private configureFeaturedFileRequirements(required: boolean): void {
    const control = this.form.controls.featuredFile;

    this.changeRequirementsForControl({ control, required });
  }

  private configureFilesRequirements(required: boolean): void {
    const control = this.form.controls.files;

    this.changeRequirementsForControl({ control, required });
  }

  private configurePriceRequirements(required: boolean): void {
    const control = this.form.controls.price;

    this.changeRequirementsForControl({ control, required });
  }

  private changeRequirementsForControl<Value>(options: {
    required: boolean;
    control: FormControl<Value>;
  }): void {
    const { control, required } = options;

    required
      ? control.setValidators([Validators.required])
      : control.removeValidators([Validators.required]);

    control.updateValueAndValidity();
  }
}
